import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { BUDDY_PAIRS, BUDDY_PAIRS_SAVE, CHAPTER_LIST_DROPDOWN } from "../../routes/routes";
import { BUDDY_PAIRS_SAVED } from "../../constant/constant";
import { NoDataFound } from "../../components/Utils";
import Loader from "../../components/common/Loader";
import { glassSelectStyles } from "../../constant/selectStyles";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { applyExcelThinBorders, styleExcelHeaderRow } from "../../utils/excelStyles";

const buddySelectStyles = {
  ...glassSelectStyles,
  control: (base, state) => ({
    ...glassSelectStyles.control(base, state),
    backgroundColor: "#ffffff",
    boxShadow: "none",
    border: state.isFocused ? "1px solid #163586" : "1px solid #e2e8f0",
  }),
};

const PREVIEW_COUNT = 7;
const CATEGORY_PREVIEW = 6;
const AVATAR = { background: "#e8edfb", color: "#163586" };

const categoryLabel = (value) => String(value || "")
  .toLowerCase()
  .replace(/(^|[^a-z0-9])([a-z])/g, (_, prefix, letter) => prefix + letter.toUpperCase());

const authUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    return {};
  }
};

const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const pairKey = (oneId, twoId) => {
  const left = Number(oneId);
  const right = Number(twoId);
  return left < right ? `${left}:${right}` : `${right}:${left}`;
};

const BuddyPairs = () => {
  const user = authUser();
  const userType = Number(user.user_type);
  const isChapterAdmin = userType === 3;
  const mustChooseChapter = [1, 2, 6, 7].includes(userType);
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(isChapterAdmin ? user.chapter_id : "");
  const [pairMonth, setPairMonth] = useState(moment().format("YYYY-MM"));
  const [board, setBoard] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [slotA, setSlotA] = useState("");
  const [slotB, setSlotB] = useState("");
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [keptKeys, setKeptKeys] = useState({});
  const [saving, setSaving] = useState(false);
  const [unpairTarget, setUnpairTarget] = useState(null);
  const [categoryTip, setCategoryTip] = useState(null);
  const [sessionPairs, setSessionPairs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!categoryTip) return undefined;
    const hide = () => setCategoryTip(null);
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [categoryTip]);

  useEffect(() => {
    if (isChapterAdmin) return;
    axios.get(CHAPTER_LIST_DROPDOWN).then((res) => {
      if (res.status === 200 && res.data.success) setChapters(res.data.data || []);
    });
  }, [isChapterAdmin]);

  const loadPairs = () => {
    if (!chapterId) {
      setBoard(null);
      setPairs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(BUDDY_PAIRS, { params: { chapter_id: chapterId, pair_month: pairMonth } })
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          setBoard(res.data.data);
          setPairs(res.data.data.pairs || []);
          setSlotA("");
          setSlotB("");
          setKeptKeys({});
        }
      })
      .catch((err) => toast.error(err.response?.data?.msg || "Unable to load pairs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setSessionPairs([]);
    loadPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, pairMonth]);

  const personOf = (id) => {
    const member = board?.members?.find((item) => item.id === Number(id));
    if (member) {
      return {
        id: member.id,
        name: member.name,
        company: member.company_name,
        category: member.category_name,
        isAdmin: false,
      };
    }
    if (board?.chapter_admin?.id === Number(id)) {
      return {
        id: board.chapter_admin.id,
        name: board.chapter_admin.name,
        company: "Assigned buddy",
        category: "",
        isAdmin: true,
      };
    }
    return { id, name: "", company: "", category: "", isAdmin: false };
  };

  const usedIds = useMemo(() => {
    const ids = new Set();
    pairs.forEach((pair) => {
      ids.add(Number(pair.user_one_id));
      ids.add(Number(pair.user_two_id));
    });
    return ids;
  }, [pairs]);

  const members = board?.members || [];
  const unpaired = members.filter((member) => !usedIds.has(member.id));
  const categories = [...new Set(members.map((member) => member.category_name).filter(Boolean))];
  const categoryPreview = showAllCategories ? categories : categories.slice(0, CATEGORY_PREVIEW);
  const visibleCategories = !showAllCategories && category !== "All" && !categoryPreview.includes(category)
    ? [category, ...categoryPreview.slice(0, CATEGORY_PREVIEW - 1)]
    : categoryPreview;
  const liveDate = moment(pairMonth, "YYYY-MM").format("D MMM YYYY");
  const previousLabel = board?.previous_month_label || moment(pairMonth, "YYYY-MM").subtract(1, "month").format("MMM");
  const query = search.trim().toLowerCase();
  const visibleUnpaired = unpaired.filter((member) => {
    const matchesCategory = category === "All" || member.category_name === category;
    const haystack = `${member.name} ${member.company_name} ${member.category_name}`.toLowerCase();
    return matchesCategory && (!query || haystack.includes(query));
  });
  const listedMembers = showAllMembers ? visibleUnpaired : visibleUnpaired.slice(0, PREVIEW_COUNT);
  const repeatCount = pairs.filter((pair) => Number(pair.repeated_last_month) === 1).length;
  const adminPairCount = pairs.filter((pair) => pair.user_one_is_admin || pair.user_two_is_admin).length;
  const pairedMembers = pairs.reduce((count, pair) => count + (pair.user_one_is_admin || pair.user_two_is_admin ? 1 : 2), 0);
  const totalMembers = members.length;
  const donePercent = totalMembers ? Math.round((pairedMembers / totalMembers) * 100) : 0;
  const slotPersonA = slotA ? personOf(slotA) : null;
  const slotPersonB = slotB ? personOf(slotB) : null;
  const historyFor = (person) => members.find((item) => item.id === Number(person?.id))?.buddy_history || [];
  const sharedHistory = slotPersonA && slotPersonB
    ? historyFor(slotPersonA).find((row) => Number(row.buddy_id) === Number(slotPersonB.id))
    : null;

  const lastMonthKeys = new Set((board?.last_month_pairs || []).map((pair) => pairKey(pair.user_one_id, pair.user_two_id)));

  const persist = (nextPairs) => {
    const seen = new Set();
    for (const pair of nextPairs) {
      const one = Number(pair.user_one_id);
      const two = Number(pair.user_two_id);
      if (seen.has(one) || seen.has(two)) {
        toast.error("This member is already paired");
        return;
      }
      seen.add(one);
      seen.add(two);
    }
    setPairs(nextPairs);
    setSlotA("");
    setSlotB("");
    setSaving(true);
    axios.post(BUDDY_PAIRS_SAVE, {
      chapter_id: Number(chapterId),
      pair_month: pairMonth,
      pairs: nextPairs.map((pair) => ({
        user_one_id: pair.user_one_id,
        user_two_id: pair.user_two_id,
      })),
    }).then((res) => {
      if (res.status === 201 && res.data.success) {
        toast.success(BUDDY_PAIRS_SAVED);
        loadPairs();
      }
    }).catch((err) => {
      toast.error(err.response?.data?.msg || "Unable to save pairs");
      loadPairs();
    }).finally(() => setSaving(false));
  };

  const addPair = (oneId, twoId) => {
    if (!board?.editable || saving || !oneId || !twoId || Number(oneId) === Number(twoId)) return;
    if (usedIds.has(Number(oneId)) || usedIds.has(Number(twoId))) {
      toast.error("This member is already paired");
      setSlotA("");
      setSlotB("");
      return;
    }
    const one = personOf(oneId);
    const two = personOf(twoId);
    setSessionPairs((current) => [{ label: `${one.name} and ${two.name}`, at: Date.now() }, ...current]);
    persist([
      ...pairs,
      {
        user_one_id: one.id,
        user_one_name: one.name,
        user_one_company: one.company,
        user_one_category: one.category,
        user_one_is_admin: one.isAdmin,
        user_two_id: two.id,
        user_two_name: two.name,
        user_two_company: two.company,
        user_two_category: two.category,
        user_two_is_admin: two.isAdmin,
        repeated_last_month: lastMonthKeys.has(pairKey(one.id, two.id)) ? 1 : 0,
      },
    ]);
  };

  const pickMember = (id) => {
    if (!board?.editable || saving) return;
    const selected = Number(id);
    if (usedIds.has(selected)) {
      toast.error("This member is already paired");
      return;
    }
    if (Number(slotA) === selected) {
      setSlotA(slotB);
      setSlotB("");
      return;
    }
    if (Number(slotB) === selected) {
      setSlotB("");
      return;
    }
    if (!slotA) {
      setSlotA(selected);
      return;
    }
    setSlotB(selected);
  };

  const exportExcel = async () => {
    if (!chapterId || !board) {
      toast.error("Select a chapter to export buddy pairs.");
      return;
    }
    const workbook = new Excel.Workbook();
    const addSheet = (name, columns, rows) => {
      const worksheet = workbook.addWorksheet(name);
      worksheet.columns = columns;
      styleExcelHeaderRow(worksheet.getRow(1), columns.length);
      rows.forEach((row, index) => worksheet.addRow({ sno: index + 1, ...row }));
      worksheet.columns.forEach((column) => {
        column.width = Math.max(18, String(column.header || "").length + 6);
      });
      applyExcelThinBorders(worksheet);
    };
    const memberPairs = pairs.filter((pair) => !pair.user_one_is_admin && !pair.user_two_is_admin);
    const adminPairs = pairs.filter((pair) => pair.user_one_is_admin || pair.user_two_is_admin);
    addSheet("Paired members", [
      { header: "S.No", key: "sno" },
      { header: "Member", key: "member" },
      { header: "Company", key: "company" },
      { header: "Category", key: "category" },
      { header: "Buddy", key: "buddy" },
      { header: "Buddy company", key: "buddy_company" },
      { header: "Buddy category", key: "buddy_category" },
      { header: "Same as last month", key: "repeat" },
    ], memberPairs.map((pair) => ({
      member: pair.user_one_name,
      company: pair.user_one_company,
      category: pair.user_one_category,
      buddy: pair.user_two_name,
      buddy_company: pair.user_two_company,
      buddy_category: pair.user_two_category,
      repeat: Number(pair.repeated_last_month) === 1 ? "Yes" : "No",
    })));
    addSheet("Unpaired members", [
      { header: "S.No", key: "sno" },
      { header: "Member", key: "member" },
      { header: "Company", key: "company" },
      { header: "Category", key: "category" },
      { header: "Last buddy", key: "last_buddy" },
    ], unpaired.map((member) => ({
      member: member.name,
      company: member.company_name,
      category: member.category_name,
      last_buddy: member.last_buddy_name || "",
    })));
    addSheet("No available pair", [
      { header: "S.No", key: "sno" },
      { header: "Member", key: "member" },
      { header: "Company", key: "company" },
      { header: "Category", key: "category" },
      { header: "Chapter admin", key: "admin" },
    ], adminPairs.map((pair) => {
      const memberIsFirst = !pair.user_one_is_admin;
      return {
        member: memberIsFirst ? pair.user_one_name : pair.user_two_name,
        company: memberIsFirst ? pair.user_one_company : pair.user_two_company,
        category: memberIsFirst ? pair.user_one_category : pair.user_two_category,
        admin: memberIsFirst ? pair.user_two_name : pair.user_one_name,
      };
    }));
    const buf = await workbook.xlsx.writeBuffer();
    const chapterName = board.chapter_name || "Chapter";
    saveAs(new Blob([buf]), `Buddy Pairs - ${chapterName} - ${moment(pairMonth, "YYYY-MM").format("MMMM YYYY")}.xlsx`);
  };

  const removePair = (pair) => {
    if (saving) return;
    persist(pairs.filter((item) => pairKey(item.user_one_id, item.user_two_id) !== pairKey(pair.user_one_id, pair.user_two_id)));
  };

  const Person = ({ name, company, categoryName, linked }) => {
    return (
      <div className={`buddy-person${linked ? " is-linked" : ""}`}>
        <span className="buddy-avatar" style={AVATAR}>{initials(name)}</span>
        <div>
          <strong>{name}</strong>
          {company ? <span>{company}</span> : null}
          {categoryName ? (
            <em
              onMouseEnter={(event) => {
                const el = event.currentTarget;
                if (el.scrollWidth <= el.clientWidth) return;
                const box = el.getBoundingClientRect();
                setCategoryTip({ text: categoryLabel(categoryName), left: box.left, top: box.bottom + 6 });
              }}
              onMouseLeave={() => setCategoryTip(null)}
            >
              {categoryLabel(categoryName)}
            </em>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Buddy Pairs</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                    </div>
                    <div><Link to="/dashboard"><span>Dashboard</span></Link></div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li><span className="active_breadcrumb">Buddy Pairs</span></li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <button type="button" className="btn rounded-pill btn-primary excel-btn ibutton" disabled={!chapterId || loading} onClick={exportExcel}>
                  <span className="tf-icons bx bxs-spreadsheet"></span>
                  &nbsp; Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1 buddy-page">
        <div className="filter-card">
          <h6 className="filter-card-title">Search &amp; Filter</h6>
          <div className="row g-3 align-items-end">
            {mustChooseChapter && (
              <div className="col-12 col-md-4">
                <label className="filter-label">Chapter</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={buddySelectStyles}
                  options={chapters}
                  isClearable
                  placeholder="Select chapter"
                  value={chapters.find((item) => item.value === Number(chapterId)) || null}
                  onChange={(selected) => {
                    setChapterId(selected?.value || "");
                    setCategory("All");
                    setSearch("");
                  }}
                />
              </div>
            )}
            <div className="col-12 col-md-3">
              <label className="filter-label">Month</label>
              <input type="month" className="form-control" value={pairMonth || moment().format("YYYY-MM")} onChange={(e) => setPairMonth(e.target.value || moment().format("YYYY-MM"))} />
            </div>
          </div>
        </div>
        {!chapterId ? (
          <div className="card icard sticky-table-card buddy-empty-stage">
            <table className="table itable">
              <tbody>
                <NoDataFound colspan="1" message="Select a chapter to see buddy pairs." />
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="card icard sticky-table-card">
            <table className="table itable">
              <tbody>
                <Loader loading colspan="1" />
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="buddy-stats">
              <div>
                <span className="buddy-stat-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                </span>
                <div>
                  <small>Total members</small>
                  <strong>{totalMembers}</strong>
                  <em>Across {categories.length} business {categories.length === 1 ? "category" : "categories"}</em>
                </div>
              </div>
              <div className="is-paired">
                <span className="buddy-stat-icon is-paired">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                </span>
                <div>
                  <small>Paired</small>
                  <strong className="is-green">{pairedMembers}</strong>
                  <div className="buddy-progress"><span style={{ width: `${donePercent}%` }} /></div>
                  <em>{pairs.length} pairs made · {donePercent}% done</em>
                </div>
              </div>
              <div>
                <span className="buddy-stat-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
                </span>
                <div>
                  <small>Unpaired</small>
                  <strong>{unpaired.length}</strong>
                  <em>Still waiting for a buddy</em>
                </div>
              </div>
              <div className={repeatCount ? "is-alert" : ""}>
                <span className="buddy-stat-icon is-alert">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                </span>
                <div>
                  <small>Repeat-pair flags</small>
                  <strong>{repeatCount}</strong>
                  <em>{repeatCount ? "Review the flagged pair" : "No repeat pairs"}</em>
                </div>
              </div>
            </div>

            <div className="buddy-sections">
              <section className="buddy-panel mt-2">
                <header>
                  <h6>Unpaired members</h6>
                  <span className="buddy-count">{unpaired.length} left</span>
                </header>
                <div className="buddy-search">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                  <input
                    placeholder="Search name or business"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="buddy-chips">
                  <button type="button" className={category === "All" ? "is-on" : ""} onClick={() => setCategory("All")}>All categories</button>
                  {visibleCategories.map((item) => (
                    <button key={item} type="button" className={item === category ? "is-on" : ""} onClick={() => setCategory(item)}>
                      {categoryLabel(item)}
                    </button>
                  ))}
                  {categories.length > CATEGORY_PREVIEW && (
                    <button type="button" className="buddy-chips-more" onClick={() => setShowAllCategories((open) => !open)}>
                      {showAllCategories ? "Show less" : "Show all"}
                    </button>
                  )}
                </div>
                <div className="buddy-unpaired-list">
                  {listedMembers.map((member) => {
                    const selected = Number(slotA) === member.id || Number(slotB) === member.id;
                    return (
                      <button
                        key={member.id}
                        type="button"
                        className={`buddy-unpaired${selected ? " is-picked" : ""}`}
                        onClick={() => pickMember(member.id)}
                      >
                        <span className="buddy-grip" aria-hidden="true" />
                        <span className="buddy-avatar" style={AVATAR}>{initials(member.name)}</span>
                        <span className="buddy-member-copy">
                          <strong>{member.name}</strong>
                          <small>{member.company_name || "—"}</small>
                        </span>
                        <span className="buddy-member-meta">
                          <em>{member.category_name ? categoryLabel(member.category_name) : "No category"}</em>
                          <small>
                            {member.last_buddy_name
                              ? `Last buddy: ${member.last_buddy_name} (${member.last_buddy_month})`
                              : "No buddy last month"}
                          </small>
                        </span>
                      </button>
                    );
                  })}
                  {!visibleUnpaired.length && <p className="buddy-empty">No unpaired members.</p>}
                </div>
                {visibleUnpaired.length > PREVIEW_COUNT && (
                  <div className="buddy-list-foot">
                    <span>Showing {listedMembers.length} of {visibleUnpaired.length}</span>
                    <button type="button" onClick={() => setShowAllMembers((open) => !open)}>
                      {showAllMembers ? "Show less" : "Show all"}
                    </button>
                  </div>
                )}
              </section>

              <section className="buddy-panel mt-2">
                <header>
                  <h6>Paired members</h6>
                  <span className="buddy-count">{pairs.filter((pair) => !pair.user_one_is_admin && !pair.user_two_is_admin).length} pairs</span>
                </header>
                <div className="buddy-pair-list">
                  {pairs.filter((pair) => !pair.user_one_is_admin && !pair.user_two_is_admin).map((pair) => {
                    const key = pairKey(pair.user_one_id, pair.user_two_id);
                    const sameCategory = pair.user_one_category && pair.user_one_category === pair.user_two_category;
                    const flagged = Number(pair.repeated_last_month) === 1;
                    const kept = Boolean(keptKeys[key]);
                    return (
                      <article key={key} className={`buddy-pair-card${flagged && !kept ? " is-repeat" : ""}`}>
                        {board?.editable && (
                          <button type="button" className="buddy-pair-remove" aria-label="Remove pair" onClick={() => setUnpairTarget(pair)}>×</button>
                        )}
                        <div className="buddy-pair-people">
                          <Person linked name={pair.user_one_name} company={pair.user_one_company} categoryName={pair.user_one_category} />
                          <span className="buddy-link" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                          </span>
                          <Person linked name={pair.user_two_name} company={pair.user_two_company} categoryName={pair.user_two_category} />
                        </div>
                        {sameCategory && (
                          <p className="buddy-info">Both members are in {categoryLabel(pair.user_one_category)}. For your information only.</p>
                        )}
                        {flagged && !kept && (
                          <div className="buddy-warn">
                            <span>Same pair as {previousLabel}. Continue anyway?</span>
                            {board?.editable && (
                              <div>
                                <button type="button" className="is-keep" onClick={() => setKeptKeys((current) => ({ ...current, [key]: true }))}>Keep anyway</button>
                                <button type="button" className="is-change" onClick={() => removePair(pair)}>Change</button>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                  {!pairs.some((pair) => !pair.user_one_is_admin && !pair.user_two_is_admin) && <p className="buddy-empty">No paired members yet.</p>}
                </div>
              </section>

              <section className="buddy-panel mt-2">
                <header>
                  <h6>No available pair</h6>
                  <span>Chapter admin</span>
                </header>
                <p className="buddy-admin-note">No available pair. Chapter admin assigned as buddy.</p>
                <div className="buddy-pair-list">
                  {pairs.filter((pair) => pair.user_one_is_admin || pair.user_two_is_admin).map((pair) => (
                    <article key={pairKey(pair.user_one_id, pair.user_two_id)} className="buddy-pair-card">
                      <div className="buddy-pair-people">
                        <Person name={pair.user_one_name} company={pair.user_one_company} categoryName={pair.user_one_category} isAdmin={pair.user_one_is_admin} />
                        <span className="buddy-link">↔</span>
                        <Person name={pair.user_two_name} company={pair.user_two_company} categoryName={pair.user_two_category} isAdmin={pair.user_two_is_admin} />
                      </div>
                      {board?.editable && (
                        <button type="button" className="buddy-more" onClick={() => removePair(pair)}>Remove</button>
                      )}
                    </article>
                  ))}
                </div>
                {unpaired.length !== 1 && !pairs.some((pair) => pair.user_one_is_admin || pair.user_two_is_admin) && (
                  <p className="buddy-empty mt-2">This is used only when one member is left over.</p>
                )}
              </section>
            </div>
            {(slotA || slotB) && (
              <div className="buddy-drawer-root">
                <button type="button" className="buddy-drawer-backdrop" aria-label="Close make a pair" onClick={() => { setSlotA(""); setSlotB(""); }} />
                <aside className="buddy-drawer" role="dialog" aria-label="Make a pair">
                  <header>
                    <h6>Make a pair</h6>
                    <button type="button" className="buddy-drawer-close" aria-label="Close" onClick={() => { setSlotA(""); setSlotB(""); }}>×</button>
                  </header>
                  <div className="buddy-drawer-body">
                  <div className={`buddy-make-slot${slotPersonA ? " is-filled" : ""}`}>
                    <small>Member A</small>
                    {slotPersonA ? <Person linked name={slotPersonA.name} company={slotPersonA.company} categoryName={slotPersonA.category} /> : <span>Pick the first unpaired member</span>}
                  </div>
                  <div className="buddy-make-link" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M7 7h11M15 4l3 3-3 3" /><path d="M17 17H6M9 14l-3 3 3 3" /></svg>
                  </div>
                  <div className={`buddy-make-slot${slotPersonB ? " is-filled" : ""}`}>
                    <small>Member B</small>
                    {slotPersonB ? <Person linked name={slotPersonB.name} company={slotPersonB.company} categoryName={slotPersonB.category} /> : <span>Pick the second unpaired member</span>}
                  </div>
                  {slotPersonA && slotPersonB && (
                    <div className="buddy-checks">
                      <h6>Check before pairing</h6>
                      <ul>
                        <li>
                          {slotPersonA.category && slotPersonB.category && slotPersonA.category !== slotPersonB.category
                            ? `Different business categories: ${categoryLabel(slotPersonA.category)} and ${categoryLabel(slotPersonB.category)}.`
                            : slotPersonA.category && slotPersonA.category === slotPersonB.category
                              ? `Same category: ${categoryLabel(slotPersonA.category)}. For your information only.`
                              : "Category is not set for one of these members."}
                        </li>
                        <li>
                          {lastMonthKeys.has(pairKey(slotA, slotB))
                            ? `They were paired together in ${previousLabel}. Continue anyway?`
                            : `Not paired together in ${previousLabel}.`}
                        </li>
                      </ul>
                    </div>
                  )}
                  {(slotPersonA || slotPersonB) && (
                    <div className="buddy-pair-history">
                      <small>Pairing history</small>
                      <strong>
                        {[slotPersonA?.name, slotPersonB?.name].filter(Boolean).join(" and ")}
                      </strong>
                      {sharedHistory && (
                        <p>They were paired together in {sharedHistory.month_label}. Continue anyway?</p>
                      )}
                      {[slotPersonA, slotPersonB].filter(Boolean).map((person) => {
                        const rows = historyFor(person);
                        const otherId = person.id === slotPersonA?.id ? slotPersonB?.id : slotPersonA?.id;
                        return (
                          <div key={person.id}>
                            <h6>{person.name.split(" ")[0]}&apos;s last 6 buddies</h6>
                            {rows.length ? rows.map((row) => (
                              <div key={`${person.id}-${row.pair_month}`} className={Number(row.buddy_id) === Number(otherId) ? "is-match" : ""}>
                                <span>{row.month_label}</span>
                                <b>{row.buddy_name}</b>
                              </div>
                            )) : <p className="buddy-empty">No earlier buddies.</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  </div>
                  <footer className="buddy-drawer-foot">
                    <button type="button" className="buddy-create" disabled={!board?.editable || saving || !slotA || !slotB} onClick={() => addPair(slotA, slotB)}>
                      Create pair
                    </button>
                    {board?.editable && unpaired.length === 1 && board.chapter_admin && (
                      <button type="button" className="buddy-admin-btn" onClick={() => addPair(unpaired[0].id, board.chapter_admin.id)}>
                        Assign chapter admin as buddy
                      </button>
                    )}
                  </footer>
                </aside>
              </div>
            )}
          </>
        )}
      </div>
      {categoryTip && (
        <div className="buddy-category-tip" style={{ left: categoryTip.left, top: categoryTip.top }}>
          {categoryTip.text}
        </div>
      )}
      <Modal show={Boolean(unpairTarget)} size="sm" centered backdrop="static" onHide={() => setUnpairTarget(null)}>
        <Modal.Header closeButton className="no-border"></Modal.Header>
        <Modal.Body className="idel_popup">
          <h6 className="text-center modal_title">
            Unpair {unpairTarget?.user_one_name} and {unpairTarget?.user_two_name}?
          </h6>
          <div className="text-center">
            <button type="button" className="btn btn-danger btn-sm me-2 iclose_btn" onClick={() => setUnpairTarget(null)}>No</button>
            <button
              type="button"
              className="btn btn-sm isubmit_button"
              onClick={() => {
                const pair = unpairTarget;
                setUnpairTarget(null);
                removePair(pair);
              }}
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BuddyPairs;
